import React, { createContext, useContext, useEffect, useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';
import { isSupabaseDatabase } from '@annote/core/dbs/supabaseDb';
import { Profile } from '@annote/types';
import { useDataContext } from './DataContext';

const setUserForSentry = (user: Profile | null): void => {
  if (user?.userId) {
    Sentry.setUser({ id: user.userId, email: user.email });
  } else {
    Sentry.setUser(null);
  }
};

interface ProfileContextType {
  profile: Profile | null;
  updateProfile: (profile: Profile) => Promise<void>;
  logout: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { annoteDB } = useDataContext();

  // Use React state for profile to enable reactive updates
  const [profile, setProfile] = useState<Profile | null>(annoteDB?.profile ?? null);

  // Sync profile state when annoteDB.profile changes (initial load, login, etc.)
  useEffect(() => {
    setProfile(annoteDB?.profile ?? null);
  }, [annoteDB?.profile]);

  const updateProfile = useCallback(
    async (updatedProfile: Profile): Promise<void> => {
      if (!annoteDB) throw new Error('annoteDB is not initialized');
      setUserForSentry(updatedProfile);
      await annoteDB.updateProfile(updatedProfile);
      // Update React state to trigger re-renders
      setProfile(updatedProfile);
    },
    [annoteDB],
  );

  useEffect(() => {
    // Set up Sentry and debug things when profile changes
    if (profile) {
      setUserForSentry(profile);

      // Set up debug things for staff
      if (profile.isStaff && annoteDB) {
        window.annoteDB = annoteDB;
        window.profile = profile;
        if (isSupabaseDatabase(annoteDB)) {
          window.supabase = annoteDB.db;
        }
      }
    } else {
      setUserForSentry(null);
    }
  }, [profile, annoteDB]);

  const logout = useCallback(async (): Promise<void> => {
    const clearLocalStorage = (): void => {
      // Clear all Supabase auth tokens (keys starting with 'sb-')
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('sb-')) {
          window.localStorage.removeItem(key);
        }
      });

      // Clear the Supabase session stored under 'annote-auth' (matches storageKey in supabase.ts)
      window.localStorage.removeItem('annote-auth');

      // Clear any Google OAuth state from localStorage
      window.localStorage.removeItem('supabase.auth.token');
      window.localStorage.removeItem('supabase.auth.refreshToken');

      // Clear redirectTo if it exists
      window.localStorage.removeItem('redirectTo');
    };

    // Clear profile state first to prevent any UI from showing stale data
    setProfile(null);

    if (!annoteDB) {
      clearLocalStorage();
      return;
    }

    const { serverDB } = annoteDB;
    if (!serverDB) {
      // No server DB available (extension mode)
      clearLocalStorage();
    } else if (!isSupabaseDatabase(serverDB)) {
      // Not a Supabase database
      clearLocalStorage();
    } else {
      // Sign out from Supabase - this is async and will trigger auth state change
      try {
        const { error } = await serverDB.db.auth.signOut();
        if (error) {
          // Log error but continue with cleanup
          console.error('[Logout] Error signing out from Supabase:', error);
        }
        // Wait a moment for auth state change to propagate
        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, 100);
        });
      } catch (error) {
        // Log error but continue with cleanup
        console.error('[Logout] Exception during signOut:', error);
      }

      // Clear database state
      if (isSupabaseDatabase(annoteDB)) {
        annoteDB.userId = undefined;
        annoteDB.profile = null;
      }

      // Clear localStorage after signOut completes
      clearLocalStorage();
    }
  }, [annoteDB]);

  return <ProfileContext.Provider value={{ profile, updateProfile, logout }}>{children}</ProfileContext.Provider>;
};

export const useProfileContext = (): ProfileContextType => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
